<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import { useActivities, type SupportedActivitiesView } from '@apparatix/composables';
import { DialogGroups } from '@apparatix/constants';
import { Stack, Toolbar } from '@apparatix/library/core';
import HistoryAccordion from './HistoryAccordion.vue';
import NoContent from '../NoContent.vue';
import { Noir } from '@apparatix/theme/Noir';
import type { Activity, ActivityModel, ActivityTypeName } from '@apparatix/schemas/Activities';
import type { Relationship } from '@apparatix/schemas/Relationship';
import { RelationshipType, viewToRelationshipType } from '@apparatix/schemas/Relationship';
import { useCreateActivity } from '@apparatix/composables/useCreateActivity';
import { ActivitySection } from './commonProps';
import { AsKeyed } from '@apparatix/types';
import ActivityCard from './ActivityCard.vue';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';

type Props = {
  parentObject: Relationship;
  sections: Array<AsKeyed<ActivitySection>>;
  view: SupportedActivitiesView;
  isLoading: boolean;
  showAddButtons?: boolean;
}

const props = defineProps<Props>();

const {
  completeActivity,
  confirmDeleteActivity,
  createOrUpdateActivity,
  deleteActivity,
  insertOrUpdateActivityInList,
  loadActivities,
  openDeferReminderDialog,
  setCurrentAction,
} = useActivities();

const { addActivityComponent, showModal } = useCreateActivity();

const model = ref<ActivityModel>();
const isCreateOrUpdateModalOpen = ref(false);

const totalActivityCount = computed(() => {
  let length = 0;
  props.sections.forEach(section => length = (section.items?.length ?? 0) + length);
  return length;
});

const showNoActivitiesComponent = computed(() => totalActivityCount.value === 0);

const parentType = computed<RelationshipType>(() => {
  try {
    return viewToRelationshipType.parse(props.view);
  } catch {
    return undefined;
  }
});

const onCreateOrUpdateModalClose = () => {
  isCreateOrUpdateModalOpen.value = false;
  model.value = undefined;
  setCurrentAction(undefined);
};

const onEditActivity = (activity: Activity) => showModal(activity.type?.name as ActivityTypeName, props.view, props.parentObject, activity);

const onCompletedChange = async (completed: boolean, activity: Activity) => {
  if (completed) {
    const updated = await completeActivity(activity);
    insertOrUpdateActivityInList(updated);
  } else {
    const payload: Partial<Activity> = { id: activity.id, completedAt: undefined };
    const updated = await createOrUpdateActivity(payload);
    insertOrUpdateActivityInList(updated);
  }
};

const acceptDeleteActivity = async (series: boolean, id: number) => {
  if (series) {
    await deleteActivity(id, true);
  } else {
    await deleteActivity(id);
  }

  if (isCreateOrUpdateModalOpen.value) {
    onCreateOrUpdateModalClose();
  }
};

const onDeleteActivity = async (id: number, series: boolean) => confirmDeleteActivity(series, () => acceptDeleteActivity(series, id));

const commonActivityItemProps = {
  parentId: props.parentObject.id,
  parentType: parentType.value,
  isInFlyout: true,
  onCompletedChange: onCompletedChange,
  onDelete: onDeleteActivity,
  onEdit: onEditActivity,
  onDefer: openDeferReminderDialog,
};

onMounted(async () => {
  await loadActivities(props.parentObject.id, props.view);
});
</script>
<template>
  <Stack>
    <Toolbar v-if="props.showAddButtons" align="left" class="mb-6">
      <Button
        label="Activity"
        icon="pi pi-plus"
        :dt="Noir.button"
        outlined
        @click="showModal('Task', props.view, props.parentObject, undefined)"
      />
      <Button
        label="Note"
        icon="pi pi-plus"
        :dt="Noir.button"
        outlined
        @click="showModal('Note', props.view, props.parentObject, undefined)"
      />
      <Button
        label="Reminder"
        icon="pi pi-plus"
        :dt="Noir.button"
        outlined
        @click="showModal('Reminder', props.view, props.parentObject, undefined)"
      />
    </Toolbar>
    <FormSkeleton v-if="props.isLoading" />
    <template v-if="!showNoActivitiesComponent && !props.isLoading">
      <HistoryAccordion
        v-for="section in props.sections"
        :key="section.key"
        :badge-severity="section.badgeSeverity"
        :title="section.label"
        :item-count="section.items?.length ?? 0"
      >
        <ActivityCard
          v-for="activity in section.items"
          :key="activity.key"
          :activity="activity"
          v-bind="commonActivityItemProps"
        />
      </HistoryAccordion>
    </template>
    <NoContent
      v-if="showNoActivitiesComponent && !props.isLoading"
      button-label="Add Activity"
      button-icon="pi pi-plus"
      @action-click="showModal('Task', props.view, props.parentObject, undefined)"
    />
  </Stack>
  <component :is="addActivityComponent" />
  <ConfirmDialog :group="DialogGroups.crm.activities.confirmDeleteActivity" />
</template>
